<template>
  <v-container class="grey-lighten-3 full-height d-flex flex-column">
    <!-- Loading State -->
    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2">Loading leave application details...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-row v-else-if="!leaveData || Object.keys(leaveData).length === 0" justify="center">
      <v-col cols="12" class="text-center">
        <p class="text-grey">No leave application data found</p>
        <v-btn color="primary" @click="goBack" class="mt-4">Go Back</v-btn>
      </v-col>
    </v-row>

    <!-- Printable Area -->
    <v-row v-else justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <div class="print-container" id="printable-area">
          <!-- Header Section -->
          <div class="header">
            <h1 class="company-name">{{ getCurrentCompany?.name || 'Company Name' }}</h1>
            <p class="company-address">{{ getCurrentCompany?.address || 'Company Address' }}</p>
          </div>

          <!-- Leave Application Title -->
          <h2 class="leave-title">Leave Application</h2>

          <!-- Main Content: Two Columns -->
          <div class="main-content">
            <!-- Left Column -->
            <div class="left-column">
              <!-- Applicant Details Box -->
              <div class="info-box">
                <div class="box-label">Applicant Details</div>
                <div class="box-content">
                  <div class="info-item">
                    <span class="label">ID:</span>
                    <span class="value">{{ leaveData.employee?.code || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Name:</span>
                    <span class="value">{{ leaveData.employee?.name || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Designation:</span>
                    <span class="value">{{ leaveData.employee?.designation?.name || leaveData.employee?.designation || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Email:</span>
                    <span class="value">{{ leaveData.employee?.email || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Site Name:</span>
                    <span class="value">{{ getBranchName() }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Contact No:</span>
                    <span class="value">{{ leaveData.employee?.mobile || leaveData.employee?.phone_office || 'N/A' }}</span>
                  </div>
                  <!-- <div class="info-item">
                    <span class="label">Address:</span>
                    <span class="value">{{ leaveData.employee?.permanent_address || leaveData.employee?.address || 'N/A' }}</span>
                  </div> -->
                </div>
              </div>

              <!-- Assignment/Reliever Details Box -->
              <div v-if="leaveData.re_leaver" class="info-box">
                <div class="box-label">Assignment</div>
                <div class="box-content">
                  <div class="info-item">
                    <span class="label">Assign ID:</span>
                    <span class="value">{{ leaveData.re_leaver?.code || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Assign Name:</span>
                    <span class="value">{{ leaveData.re_leaver?.name || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Designation:</span>
                    <span class="value">{{ leaveData.re_leaver?.designation?.name || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Assign Contact:</span>
                    <span class="value">{{ leaveData.re_leaver?.mobile || leaveData.re_leaver?.phone_office || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column -->
            <div class="right-column">
              <!-- Leave Details Box -->
              <div class="info-box">
                <div class="box-label">Leave Details</div>
                <div class="box-content">
                  <div class="info-item">
                    <span class="label">Leave Code:</span>
                    <span class="value">{{ leaveData?.code || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Leave Type:</span>
                    <span class="value">{{ leaveData.leave_type?.name || leaveData.leave_type?.code || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Leave Start Date:</span>
                    <span class="value">{{ formatDateView(leaveData.start_date) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Leave End Date:</span>
                    <span class="value">{{ formatDateView(leaveData.end_date) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Total Apply Days:</span>
                    <span class="value">{{ calculateTotalDays() }}</span>
                  </div>
                </div>
              </div>

              <!-- Reason Box -->
              <div class="info-box">
                <div class="box-label">Reason</div>
                <div class="box-content reason-box">
                  {{ leaveData.reason || 'N/A' }}
                </div>
              </div>

              <!-- Supervisor Details Box -->
              <div v-if="getSupervisorInfo()" class="info-box">
                <div class="box-label">Supervisor</div>
                <div class="box-content">
                  <div class="info-item">
                    <span class="label">Supervisor ID:</span>
                    <span class="value">{{ leaveData.employee?.hod?.code || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Supervisor Name:</span>
                    <span class="value">{{ leaveData.employee?.hod?.name || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Designation:</span>
                    <span class="value">{{ leaveData.employee?.hod?.designation?.name || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Supervisor Contact:</span>
                    <span class="value">{{ leaveData.employee?.hod?.mobile || leaveData.employee?.hod?.phone_office || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Approval Section -->
          <div class="approval-section">
            <div class="approval-box">
              <div class="box-label">Approve By</div>
              <div class="box-content">
                {{ getApprovedByName() }} - {{ getApprovedByDesignation() }}
              </div>
            </div>
            <div class="approval-box">
              <div class="box-label">Approval Status</div>
              <div class="box-content">
                <span :class="getApprovalStatusClass(leaveData.approval_status)">
                  {{ leaveData.approval_status || 'Pending' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer">
            It is a software generated report
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Action Toolbar at Bottom (Hidden on Print) -->
    <v-row class="d-print-none mb-4" justify="center" v-if="!loading && leaveData">
      <v-col cols="12">
        <div class="text-center">
          <v-btn v-if="leaveData.approval_current_process && leaveData.is_progress" color="warning" class="mr-2" @click="viewApproval">
            <v-icon left>mdi-progress-clock</v-icon> Approval Progress
          </v-btn>
          <v-btn color="primary" class="mr-2" @click="printPage">
            <v-icon left>mdi-printer</v-icon> Print
          </v-btn>
          <v-btn color="success" @click="downloadPDF">
            <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import html2pdf from "html2pdf.js";
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import { formatDateView, formatDateDash } from "@/plugins/helpers";

export default {
  name: "ViewLeave",
  methods: {
    formatDateView,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const loading = ref(false);
    const leaveData = ref({});

    const calculateTotalDays = () => {
      // Use details array if available (more accurate)
      if (leaveData.value.details && Array.isArray(leaveData.value.details) && leaveData.value.details.length > 0) {
        const total = leaveData.value.details.reduce((sum, detail) => {
          return sum + parseFloat(detail.counted || 0);
        }, 0);
        return total.toFixed(2);
      }
      
      // Fallback to date calculation
      if (!leaveData.value.start_date || !leaveData.value.end_date) return '0.00';
      
      const start = new Date(leaveData.value.start_date);
      const end = new Date(leaveData.value.end_date);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      
      // Adjust for half days
      let totalDays = diffDays;
      if (leaveData.value.start_day_status === 'first_half' || leaveData.value.start_day_status === 'second_half') {
        totalDays = diffDays - 0.5;
      }
      if (leaveData.value.end_day_status === 'first_half' || leaveData.value.end_day_status === 'second_half') {
        totalDays = diffDays - 0.5;
      }
      
      return totalDays.toFixed(2);
    };

    const getApprovalStatusClass = (status) => {
      if (!status) return 'status-pending';
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === 'approved') return 'status-approved';
      if (lowerStatus === 'pending') return 'status-pending';
      return 'status-rejected';
    };

    const getBranchName = () => {
      if (leaveData.value.branch?.name) {
        return leaveData.value.branch.name;
      }
      const currentBranch = store.getters.getCurrentBranch;
      if (currentBranch && currentBranch.name) {
        return currentBranch.name;
      }
      return 'N/A';
    };

    const getSupervisorInfo = () => {
      // Check if supervisor info is available in approval_current_process
      //console.log(leaveData.value.approval_current_process);
      if (leaveData.value.approval_current_process?.employee) {
        return leaveData.value.approval_current_process.employee;
      }
      // Check if employee has hod (Head of Department)
      if (leaveData.value.employee?.hod) {
        return leaveData.value.employee.hod;
      }
      // Return null if no supervisor info available
      return null;
    };

    const getApprovedByName = () => {
      if (leaveData.value.approval_current_process?.approved_by?.full_name) {
        return leaveData.value.approval_current_process.approved_by.full_name;
      }
      // if (leaveData.value.approval_current_process?.approval_step_name?.name) {
      //   return leaveData.value.approval_current_process.approval_step_name.name;
      // }
      // If approved, show created by as fallback
      if (leaveData.value.approval_status?.toLowerCase() === 'approved' && leaveData.value.created_by?.full_name) {
        return leaveData.value.created_by.full_name;
      }
      return 'N/A';
    };

    const getApprovedByDesignation = () => {
      if (leaveData.value.approval_current_process?.approved_by?.employee?.designation?.name) {
        return leaveData.value.approval_current_process?.approved_by?.employee?.designation?.name;
      }
      // if (leaveData.value.approval_current_process?.approval_step_name?.name) {
      //   return leaveData.value.approval_current_process.approval_step_name.name;
      // }
      // If approved, show created by as fallback
      if (leaveData.value.approval_status?.toLowerCase() === 'approved' && leaveData.value.created_by?.employee?.designation?.name) {
        return leaveData.value.created_by?.employee?.designation?.name;
      }
      return 'N/A';
    };

    const loadLeaveData = async (id) => {
      if (!id) {
        toast.error("No Leave Application ID provided");
        router.push({ path: "/leave/list" });
        return;
      }

      loading.value = true;
      try {
        const data = await store.dispatch("getLeaveApplicationById", id);
        leaveData.value = data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load leave application data");
        router.push({ path: "/leave/list" });
      } finally {
        loading.value = false;
      }
    };

    const goBack = () => {
      router.push({ path: "/leave/application-list" });
    };

    const downloadPDF = async () => {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: [0.5, 0.5],
        filename: `leave-application-${leaveData.value.code || leaveData.value.id}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };
      await html2pdf().set(opt).from(element).save();
    };

    const viewApproval = () => {
        router.push({
          name: "approval-process-preview",
          params: { id: leaveData.value.id },
          query: {
            model: "ApprovalProcess",
            page_title: leaveData.value?.approval_current_process?.page_title,
            referenceable_type: leaveData.value?.approval_current_process?.referenceable_type,
            referenceable_id: leaveData.value?.approval_current_process?.referenceable_id,
          },
      })
      .then(() => {
        console.log("Navigation successful");
      })
      .catch((error) => {
        console.error("Navigation error:", error);
        toast.error("Navigation failed. Check console/network for details.");
      });
    };

    const printPage = () => {
      const printContent = document.getElementById("printable-area").innerHTML;
      const printWindow = window.open("", "_blank", `width=${screen.width}, height=${screen.height}, left=0, top=0`);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Leave Application - ${leaveData.value.code || 'LA'}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              background: #fff;
              margin: 0;
              padding: 0;
            }
            .print-container {
              width: 800px;
              margin: auto;
              padding: 25px;
              position: relative;
            }
            .header {
              text-align: center;
              border-bottom: 1px solid #333;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .company-name {
              font-size: 18px;
              font-weight: bold;
              margin: 0 0 5px 0;
            }
            .company-address {
              font-size: 12px;
              margin: 0;
            }
            .leave-title {
              text-align: center;
              margin: 15px 0;
              font-size: 16px;
              font-weight: bold;
            }
            .main-content {
              display: flex;
              justify-content: space-between;
              gap: 15px;
              margin-bottom: 20px;
            }
            .left-column, .right-column {
              width: 48%;
            }
            .info-box {
              border: 1px solid #333;
              margin-bottom: 15px;
            }
            .box-label {
              background: #f0f0f0;
              padding: 5px 8px;
              font-weight: bold;
              font-size: 12px;
              border-bottom: 1px solid #333;
            }
            .box-content {
              padding: 8px;
              font-size: 12px;
            }
            .info-item {
              margin-bottom: 5px;
            }
            .label {
              font-weight: bold;
              display: inline-block;
              min-width: 100px;
            }
            .value {
              display: inline-block;
            }
            .reason-box {
              min-height: 60px;
            }
            .approval-section {
              display: flex;
              justify-content: space-between;
              gap: 15px;
              margin-top: 20px;
            }
            .approval-box {
              width: 48%;
              border: 1px solid #333;
            }
            .footer {
              text-align: center;
              margin-top: 20px;
              font-size: 10px;
              font-style: italic;
            }
            .status-approved {
              color: #4caf50;
              font-weight: bold;
            }
            .status-pending {
              color: #ff9800;
              font-weight: bold;
            }
            .status-rejected {
              color: #f44336;
              font-weight: bold;
            }
            @media print {
              .print-container {
                width: 100%;
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.moveTo(0, 0);
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 1000);
    };

    onMounted(() => {
      if (route.params.id) {
        loadLeaveData(route.params.id);
      } else {
        toast.error("No Leave Application ID provided");
        router.push({ path: "/leave/application-list" });
      }
    });

    return {
      loading,
      leaveData,
      calculateTotalDays,
      getApprovalStatusClass,
      getBranchName,
      getSupervisorInfo,
      getApprovedByName,
      getApprovedByDesignation,
      goBack,
      downloadPDF,
      printPage,
      viewApproval,
      getCurrentCompany: computed(() => store.getters.getCurrentCompany),
      getFileServerUrl: computed(() => store.getters.getFileServerUrl),
    };
  },
};
</script>

<style scoped>
.print-container {
  width: 800px;
  margin: auto;
  padding: 25px;
  background: white;
  position: relative;
  min-height: 600px;
}

.header {
  text-align: center;
  border-bottom: 1px solid #333;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.company-name {
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 5px 0;
  color: #333;
}

.company-address {
  font-size: 12px;
  margin: 0;
  color: #555;
}

.leave-title {
  text-align: center;
  margin: 15px 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.main-content {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 20px;
}

.left-column,
.right-column {
  width: 48%;
}

.info-box {
  border: 1px solid #333;
  margin-bottom: 15px;
}

.box-label {
  background: #f0f0f0;
  padding: 5px 8px;
  font-weight: bold;
  font-size: 12px;
  border-bottom: 1px solid #333;
  color: #333;
}

.box-content {
  padding: 8px;
  font-size: 12px;
  color: #555;
}

.info-item {
  margin-bottom: 5px;
  line-height: 1.6;
}

.label {
  font-weight: bold;
  display: inline-block;
  min-width: 120px;
  color: #333;
}

.value {
  display: inline-block;
  color: #555;
}

.reason-box {
  min-height: 60px;
  word-wrap: break-word;
}

.approval-section {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 20px;
}

.approval-box {
  width: 48%;
  border: 1px solid #333;
}

.status-approved {
  color: #4caf50;
  font-weight: bold;
}

.status-pending {
  color: #ff9800;
  font-weight: bold;
}

.status-rejected {
  color: #f44336;
  font-weight: bold;
}

.footer {
  text-align: center;
  margin-top: 20px;
  font-size: 10px;
  font-style: italic;
  color: #666;
}

.full-height {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

@media print {
  .d-print-none {
    display: none !important;
  }
  .print-container {
    width: 100%;
    padding: 20px;
    box-shadow: none;
  }
  .info-box,
  .approval-box {
    page-break-inside: avoid;
  }
}
</style>
